<template>
  <teleport to="body">
    <div
      v-if="open"
      class="modal fade show"
      style="display: block;"
      id="cobroPedidoInlineModal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cobroPedidoInlineModalLabel"
    >
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="cobroPedidoInlineModalLabel">
              Cobrar pedido #{{ pedidoId }}
            </h5>
            <button type="button" class="btn-close" aria-label="Close" @click="handleClose"></button>
          </div>
          <div class="modal-body">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <div>
                <div class="text-muted small">Cliente</div>
                <div class="fw-semibold">{{ nombreCliente || 'Consumidor Final' }}</div>
              </div>
              <div class="text-end">
                <div class="text-muted small">Total</div>
                <div class="fs-4 fw-bold">{{ formatCurrency(total) }}</div>
              </div>
            </div>

            <div v-if="errorMessage" class="alert alert-danger" role="alert">
              {{ errorMessage }}
            </div>

            <SelectorComprobante v-model="comprobante" :documento="documento" />

            <PaymentForms
              :total="total"
              :types="paymentTypes"
              v-model:payments="payments"
              cash-received-mode
              enforce-total
              @update:summary="summary = $event"
              @submit-request="submitCobro"
            />
          </div>
          <div class="modal-footer">
            <span v-if="!canSubmit && !hasCredito" class="me-auto text-danger small">
              <i class="bi bi-exclamation-circle me-1"></i>{{ blockReason }}
            </span>
            <button class="btn btn-outline-secondary" :disabled="submitting" @click="handleClose">
              Cancelar <kbd class="ms-1 d-none d-lg-inline">Esc</kbd>
            </button>
            <button
              ref="submitButton"
              class="btn btn-success"
              :disabled="!canSubmit || submitting"
              @click="submitCobro"
            >
              <span v-if="submitting" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
              <i v-else class="bi bi-check2-circle me-1"></i>Registrar cobro <kbd class="ms-1 d-none d-lg-inline">F9</kbd>
            </button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="open" class="modal-backdrop fade show" @click="handleClose"></div>
  </teleport>
</template>

<script setup>
import { ref, computed, watch, onBeforeUnmount } from 'vue'
import PaymentForms from '@/components/PaymentForms.vue'
import SelectorComprobante from '@/components/SelectorComprobante.vue'
import apiService from '@/services/apiService.js'
import keyboardShortcutService from '@/services/keyboardShortcutService'

/* global defineProps, defineEmits */
const props = defineProps({
  open: { type: Boolean, default: false },
  movimientoId: { type: Number, default: null },
  pedidoId: { type: [Number, String], default: null },
  total: { type: Number, required: true },
  nombreCliente: { type: String, default: '' },
  // El documento del pedido: decide si se ofrece el ticket (P2 RN-VI1).
  documento: { type: String, default: '' }
})

const emit = defineEmits(['cobrado', 'cerrado'])

const paymentTypes = ['Efectivo', 'Tarjeta', 'Cheque', 'Transferencia', 'Credito']
const payments = ref([])
const submitting = ref(false)
const errorMessage = ref('')
const submitButton = ref(null)
const summary = ref(null)
const comprobante = ref('FACTURA')

const canAddPayment = computed(() => payments.value.length > 0)
const hasCredito = computed(() => payments.value.some((p) => (p.type || '').toUpperCase() === 'CREDITO'))

// El crédito no se valida contra el total (lo resuelve el backend contra el
// cupo del cliente); el contado sí, para no gastar un round trip hasta el
// servidor en un `400 monto_insuficiente` previsible
// (SPEC_COBRO_PERFORMANCE_v0.1.md, RF-7).
const canSubmit = computed(() => {
  if (!canAddPayment.value) return false
  if (hasCredito.value) return true
  return Boolean(summary.value?.valid)
})

const formatCurrencyShort = (value) => new Intl.NumberFormat('es-PY').format(Math.round(Number(value) || 0))

const blockReason = computed(() => {
  if (!canAddPayment.value) return 'Cargá con cuánto paga el cliente.'
  if (summary.value?.invalidChangeMethods?.length) {
    return `No se puede dar vuelto en ${summary.value.invalidChangeMethods.join(' / ').toLowerCase()}.`
  }
  if (!summary.value?.sufficient) {
    return `Faltan ₲ ${formatCurrencyShort(summary.value?.remaining)} para cubrir el total.`
  }
  return ''
})

function formatCurrency(value) {
  return new Intl.NumberFormat('es-PY', {
    style: 'currency',
    currency: 'PYG'
  }).format(Math.round(Number(value || 0)))
}

function resolveApiMessage(err, fallback) {
  return err?.response?.data?.message
    || err?.response?.data?.details?.error
    || err?.response?.data?.error
    || err?.message
    || fallback
}

let unregisterShortcuts = null

watch(() => props.open, (isOpen) => {
  if (isOpen) {
    errorMessage.value = ''
    comprobante.value = 'FACTURA'
    payments.value = [{ type: 'Efectivo', amount: Number(props.total || 0) }]
    summary.value = null
    // El foco va al campo de "¿Con cuánto paga?" (lo toma `PaymentForms` al
    // montarse), no al botón: es el primer dato que carga el cajero.
    unregisterShortcuts = keyboardShortcutService.register({
      scopeId: 'cobro-pedido-inline-modal',
      bindings: [
        { key: 'F9', run: submitCobro, enabled: () => canSubmit.value && !submitting.value, label: 'Registrar cobro' },
        { key: 'Enter', ctrlKey: true, run: submitCobro, enabled: () => canSubmit.value && !submitting.value, label: 'Registrar cobro' },
        { key: 'Escape', run: handleClose, enabled: () => !submitting.value, label: 'Cerrar' }
      ]
    })
  } else if (unregisterShortcuts) {
    unregisterShortcuts()
    unregisterShortcuts = null
  }
})

onBeforeUnmount(() => {
  if (unregisterShortcuts) {
    unregisterShortcuts()
    unregisterShortcuts = null
  }
})

function handleClose() {
  if (submitting.value) return
  emit('cerrado')
}

async function submitCobro() {
  if (!canSubmit.value || submitting.value) return
  if (!props.movimientoId) {
    errorMessage.value = 'No se pudo identificar el movimiento de caja del pedido.'
    return
  }

  const formaOperacion = hasCredito.value ? 'CREDITO' : 'CONTADO'
  const pagos = hasCredito.value ? [] : payments.value.map((p) => ({
    metodo_pago: String(p.type || '').toUpperCase(),
    monto: Number(p.amount || 0),
    referencia_externa: null
  }))

  const payload = {
    movimiento_id: props.movimientoId,
    forma_operacion: formaOperacion,
    pagos,
    idempotency_key: `pedido-${props.pedidoId}-${props.movimientoId}`,
    comprobante: comprobante.value
  }

  submitting.value = true
  errorMessage.value = ''
  try {
    const resp = await apiService.post('/api/cashbox/collect-sale', payload)
    const data = resp?.data?.data || {}
    emit('cobrado', {
      id_venta: data.id_venta ?? null,
      nro_factura: data.nro_factura ?? null,
      vuelto: Number(data.vuelto || 0),
      fiscal: data.fiscal ?? null,
      // P2: con ticket no hay factura; el aviso de impresión lo dice así.
      comprobante: data.comprobante || 'FACTURA',
      // Viene armado desde el cobro cuando el documento fiscal ya estaba listo
      // (RF-13): imprimir con esto ahorra un viaje al servidor.
      printJob: data.print_job ?? null,
      // COP-7A-007: link de seguimiento + contacto del cliente ya resuelto
      // por el backend (pedido > ficha del cliente > venta). Viene siempre
      // que el entorno tenga base pública configurada o derivable, sea que
      // la factura se emitió o que quedó en contingencia.
      trackingUrl: data.tracking_url ?? null,
      trackingContacto: data.tracking_contacto ?? null
    })
  } catch (err) {
    errorMessage.value = resolveApiMessage(err, 'Error al registrar el cobro.')
  } finally {
    submitting.value = false
  }
}
</script>
